import React, { useEffect, useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Database, Trash2, Search, RefreshCcw, LayoutGrid, Home, Download, Upload as UploadIcon } from "lucide-react";
import { BoardIcon } from "@/lib/boardIcons";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const AdminDatabase = ({ embedded = false }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState("snapshot"); // snapshot | properties | boards
  const [snap, setSnap] = useState(null);
  const [properties, setProperties] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProps, setSelectedProps] = useState(new Set());
  const [selectedBoards, setSelectedBoards] = useState(new Set());
  const [propQ, setPropQ] = useState("");
  const [propBoard, setPropBoard] = useState("");
  const [propCitta, setPropCitta] = useState("");
  const [propMacro, setPropMacro] = useState("");
  const [boardQ, setBoardQ] = useState("");
  const [confirmProps, setConfirmProps] = useState(false);
  const [confirmBoards, setConfirmBoards] = useState(false);
  const restoreInputRef = React.useRef(null);
  const [exporting, setExporting] = useState(false);

  const loadSnap = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/db/snapshot"); setSnap(r.data); }
    catch (e) { toast.error("Errore caricamento snapshot"); }
    finally { setLoading(false); }
  };
  const loadProperties = async () => {
    setLoading(true);
    try {
      const params = {};
      if (propQ) params.q = propQ;
      if (propBoard) params.board_id = propBoard;
      if (propCitta) params.citta = propCitta;
      if (propMacro) params.macro_area = propMacro;
      const r = await api.get("/admin/properties", { params });
      setProperties(r.data.items);
      setSelectedProps(new Set());
    } catch { toast.error("Errore caricamento immobili"); }
    finally { setLoading(false); }
  };
  const loadBoards = async () => {
    setLoading(true);
    try {
      const params = {};
      if (boardQ) params.q = boardQ;
      const r = await api.get("/admin/boards", { params });
      setBoards(r.data.items);
      setSelectedBoards(new Set());
    } catch { toast.error("Errore caricamento schede"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (tab === "snapshot") loadSnap();
    else if (tab === "properties") loadProperties();
    else if (tab === "boards") loadBoards();
    // eslint-disable-next-line
  }, [tab]);

  if (user && user.role !== "admin" && !embedded) return <Navigate to="/boards" replace />;

  const doBulkDelProps = async () => {
    try {
      const r = await api.post("/admin/properties/bulk-delete", { ids: Array.from(selectedProps) });
      toast.success(`${r.data.deleted} immobili eliminati`);
      setConfirmProps(false);
      loadProperties();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const doBulkDelBoards = async () => {
    try {
      const r = await api.post("/admin/boards/bulk-delete", { ids: Array.from(selectedBoards) });
      toast.success(`${r.data.deleted_boards} schede + ${r.data.deleted_properties} immobili eliminati`);
      setConfirmBoards(false);
      loadBoards();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const toggleP = (id) => setSelectedProps((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleB = (id) => setSelectedBoards((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  // --- Backup / Restore per scheda ----------------------------------------
  const downloadBoardBackup = async (b) => {
    try {
      const r = await api.get(`/admin/boards/${b.id}/backup`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (b.name || "board").replace(/[^\w\-]+/g, "_");
      a.href = url;
      a.download = `immomap-backup-${safeName}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup scaricato · ${r.data.counts?.properties ?? 0} immobili`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore backup");
    }
  };

  const uploadBoardBackup = async (file) => {
    if (!file) return;
    if (!/\.json$/i.test(file.name)) { toast.error("Serve un file .json"); return; }
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed?.board || !Array.isArray(parsed?.properties)) {
        toast.error("Backup non valido — manca board o properties");
        return;
      }
      const r = await api.post("/admin/boards/restore", { backup: parsed });
      toast.success(`Scheda ripristinata "${r.data.board_name}" · ${r.data.inserted} immobili`);
      loadBoards();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Errore ripristino");
    }
  };

  const downloadFullBackup = async () => {
    const toastId = toast.loading("Genero backup completo di tutte le schede…");
    try {
      const r = await api.get("/admin/db/backup-all");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `immomap-full-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const c = r.data.counts || {};
      toast.success(`Backup completo scaricato · ${c.boards ?? 0} schede · ${c.properties ?? 0} immobili`, { id: toastId });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore backup completo", { id: toastId });
    }
  };
  const exportFullDb = async () => {
    setExporting(true);
    const toastId = toast.loading("Esporto il database completo…");
    try {
      const r = await api.get("/admin/db/export-full", { responseType: "blob", timeout: 0 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `immomap-db-export-${new Date().toISOString().slice(0, 16).replace(/[T:]/g, "-")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Database esportato · ${(r.data.size / 1048576).toFixed(1)} MB`, { id: toastId });
    } catch (e) {
      toast.error("Errore durante l'export del database", { id: toastId });
    } finally { setExporting(false); }
  };
  const triggerRestore = () => restoreInputRef.current?.click();

  const allPropsSelected = properties.length > 0 && properties.every((p) => selectedProps.has(p.id));
  const allBoardsSelected = boards.length > 0 && boards.every((b) => selectedBoards.has(b.id));

  const Tab = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`tab-${id}`}
      className={`flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-medium transition-colors ${
        tab === id ? "bg-slate-900 text-white shadow" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <div className={embedded ? "" : "min-h-screen"}>
      {!embedded && <AppHeader />}
      <div className={embedded ? "" : "max-w-7xl mx-auto p-6"}>
        <div className="mb-6">
          {!embedded && <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Amministrazione</div>}
          {!embedded && <h1 className="font-display font-bold text-4xl tracking-tighter leading-none mt-1">Gestione database</h1>}
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Snapshot in tempo reale, gestione massiva di immobili e schede con filtri. Le operazioni di eliminazione sono <b>irreversibili</b>.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          <Tab id="snapshot" label="Snapshot DB" icon={Database} />
          <Tab id="properties" label="Immobili" icon={Home} />
          <Tab id="boards" label="Schede" icon={LayoutGrid} />
        </div>

        {tab === "snapshot" && (
          <div className="ios-card p-6 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="db-export-card">
            <div>
              <h2 className="font-display font-bold text-xl tracking-tight">Esporta database completo</h2>
              <p className="text-sm text-slate-500 mt-1 max-w-xl">
                Scarica un unico file JSON con tutte le collezioni (immobili, schede, utenti, community, piani, impostazioni…). Include anche dati sensibili come password cifrate e sessioni: conservalo in un luogo sicuro.
              </p>
            </div>
            <Button onClick={exportFullDb} disabled={exporting} className="rounded-full h-10 px-5 bg-slate-900 hover:bg-slate-800 text-white shrink-0" data-testid="db-export-full-btn">
              <Download className="w-4 h-4 mr-2" /> {exporting ? "Esportazione…" : "Esporta tutto"}
            </Button>
          </div>
        )}

        {tab === "snapshot" && (
          <div className="ios-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl tracking-tight">Conteggio collezioni</h2>
              <Button variant="outline" size="sm" className="rounded-full" onClick={loadSnap} data-testid="refresh-snap">
                <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Aggiorna
              </Button>
            </div>
            {loading || !snap ? (
              <div className="text-sm text-slate-500">Caricamento…</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="snap-grid">
                  {["users", "boards", "properties", "notifications", "donations", "subscriptions", "user_sessions", "presence", "geocode_cache"].map((k) => (
                    <div key={k} className="ios-card p-4 bg-slate-50 border border-slate-100">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{k.replace("_", " ")}</div>
                      <div className="font-mono-num font-bold text-2xl mt-1 text-slate-900">{snap[k] ?? 0}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 text-[11px] text-slate-500">
                  Ultimo refresh: {snap.timestamp?.slice(0, 19).replace("T", " ")}
                </div>
              </>
            )}
          </div>
        )}

        {tab === "properties" && (
          <div className="ios-card p-4">
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={propQ} onChange={(e) => setPropQ(e.target.value)} placeholder="Cerca indirizzo, città, macro area, agenzia…" className="rounded-xl bg-slate-100 border-0 pl-9 h-9" data-testid="adm-prop-q" />
              </div>
              <Input value={propBoard} onChange={(e) => setPropBoard(e.target.value)} placeholder="board_id" className="w-40 rounded-xl bg-slate-100 border-0 h-9" data-testid="adm-prop-board" />
              <Input value={propCitta} onChange={(e) => setPropCitta(e.target.value)} placeholder="Città" className="w-32 rounded-xl bg-slate-100 border-0 h-9" data-testid="adm-prop-citta" />
              <Input value={propMacro} onChange={(e) => setPropMacro(e.target.value)} placeholder="Macro area" className="w-36 rounded-xl bg-slate-100 border-0 h-9" data-testid="adm-prop-macro" />
              <Button variant="outline" className="rounded-full h-9" onClick={loadProperties} data-testid="adm-prop-search">Applica</Button>
              {selectedProps.size > 0 && (
                <Button size="sm" className="rounded-full h-9 bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setConfirmProps(true)} data-testid="adm-prop-bulk">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina {selectedProps.size} immobili
                </Button>
              )}
            </div>
            <div className="max-h-[65vh] overflow-auto">
              <table className="w-full data-table text-xs" data-testid="adm-prop-table">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>
                      <input type="checkbox" checked={allPropsSelected} onChange={() => {
                        if (allPropsSelected) setSelectedProps(new Set());
                        else setSelectedProps(new Set(properties.map((p) => p.id)));
                      }} className="w-4 h-4 accent-[#007AFF]" data-testid="adm-prop-all" />
                    </th>
                    <th>Città</th><th>Macro</th><th>Indirizzo</th>
                    <th className="num">MQ</th><th className="num">Prezzo</th>
                    <th>Scheda</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={7} className="py-6 text-center text-slate-500">Caricamento…</td></tr>}
                  {!loading && properties.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">Nessun immobile.</td></tr>}
                  {!loading && properties.map((p) => (
                    <tr key={p.id} className={selectedProps.has(p.id) ? "bg-[#007AFF]/5" : ""}>
                      <td><input type="checkbox" checked={selectedProps.has(p.id)} onChange={() => toggleP(p.id)} className="w-4 h-4 accent-[#007AFF]" data-testid={`adm-prop-sel-${p.id}`} /></td>
                      <td className="uppercase">{p.citta}</td>
                      <td className="uppercase text-slate-600">{p.macro_area}</td>
                      <td className="font-medium">{p.indirizzo}</td>
                      <td className="num">{p.mq}</td>
                      <td className="num">{fmtEuro(p.prezzo)}</td>
                      <td className="text-slate-500">{p.board_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "boards" && (
          <div className="ios-card p-4">
            <div className="flex flex-wrap gap-2 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={boardQ} onChange={(e) => setBoardQ(e.target.value)} placeholder="Cerca nome/località…" className="rounded-xl bg-slate-100 border-0 pl-9 h-9" data-testid="adm-board-q" />
              </div>
              <Button variant="outline" className="rounded-full h-9" onClick={loadBoards} data-testid="adm-board-search">Applica</Button>
              <Button
                variant="outline"
                className="rounded-full h-9 border-[#007AFF]/40 text-[#007AFF] hover:bg-[#007AFF]/5"
                onClick={downloadFullBackup}
                data-testid="adm-full-backup-btn-2"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Backup completo
              </Button>
              <Button variant="outline" className="rounded-full h-9" onClick={triggerRestore} data-testid="adm-board-restore-btn">
                <UploadIcon className="w-3.5 h-3.5 mr-1" /> Ripristina backup
              </Button>
              <input
                ref={restoreInputRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadBoardBackup(f); }}
                data-testid="adm-board-restore-input"
              />
              {selectedBoards.size > 0 && (
                <Button size="sm" className="rounded-full h-9 bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setConfirmBoards(true)} data-testid="adm-board-bulk">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina {selectedBoards.size} schede
                </Button>
              )}
            </div>
            <div className="max-h-[65vh] overflow-auto">
              <table className="w-full data-table text-xs" data-testid="adm-board-table">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>
                      <input type="checkbox" checked={allBoardsSelected} onChange={() => {
                        if (allBoardsSelected) setSelectedBoards(new Set());
                        else setSelectedBoards(new Set(boards.map((b) => b.id)));
                      }} className="w-4 h-4 accent-[#007AFF]" data-testid="adm-board-all" />
                    </th>
                    <th>Nome</th><th>Località</th><th>Owner</th>
                    <th className="num">Immobili</th><th className="num">Membri</th><th>Vacante</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={8} className="py-6 text-center text-slate-500">Caricamento…</td></tr>}
                  {!loading && boards.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-slate-500">Nessuna scheda.</td></tr>}
                  {!loading && boards.map((b) => (
                    <tr key={b.id} className={selectedBoards.has(b.id) ? "bg-[#007AFF]/5" : ""}>
                      <td><input type="checkbox" checked={selectedBoards.has(b.id)} onChange={() => toggleB(b.id)} className="w-4 h-4 accent-[#007AFF]" data-testid={`adm-board-sel-${b.id}`} /></td>
                      <td className="font-medium">
                        <span className="inline-flex items-center gap-2">
                          <BoardIcon value={b.emoji} size={18} />
                          {b.name}
                        </span>
                      </td>
                      <td className="text-slate-600">{b.location || "—"}</td>
                      <td className="text-slate-600">{b.owner?.name || b.owner?.email || "—"}</td>
                      <td className="num">{b.property_count}</td>
                      <td className="num">{b.members_count}</td>
                      <td>{b.vacant && <Badge className="rounded-full bg-amber-100 text-amber-800 text-[9px] uppercase tracking-widest border-0">Vacante</Badge>}</td>
                      <td className="text-center">
                        <button
                          type="button"
                          onClick={() => downloadBoardBackup(b)}
                          data-testid={`adm-board-backup-${b.id}`}
                          title="Scarica backup JSON di questa scheda"
                          className="inline-flex items-center justify-center h-7 w-7 rounded-full text-slate-500 hover:text-[#007AFF] hover:bg-[#007AFF]/10 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={confirmProps} onOpenChange={setConfirmProps}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {selectedProps.size} immobili?</AlertDialogTitle>
            <AlertDialogDescription>Operazione irreversibile. Le schede a cui appartengono NON verranno toccate.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={doBulkDelProps} data-testid="confirm-adm-prop-bulk">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmBoards} onOpenChange={setConfirmBoards}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {selectedBoards.size} schede?</AlertDialogTitle>
            <AlertDialogDescription>Verranno cancellati anche <b>tutti gli immobili</b> di queste schede. Irreversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-rose-600 hover:bg-rose-700" onClick={doBulkDelBoards} data-testid="confirm-adm-board-bulk">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDatabase;
